import React from 'react'
import PropTypes from 'prop-types'
import { useTranslate } from '@ds/comp-private'
import { dataProps, onProps } from '@ds/react-utils'
import { Close, CloseOlive, CloseSmall, CloseXlargeOlive } from '@ds/icons'
import { useThemeStyles } from '../../../theming'
import { CustomPropTypes } from '../../../support'
import type { EventListenerProps, ButtonForwardRef } from '../../../types'
import { IconWithTheme } from '../IconWithTheme'
import { IconButton } from '../../../components/IconButton'
import styles from './styles'

export interface PopoverCloseProps
  extends EventListenerProps<HTMLButtonElement> {
  /**
   * The text to present to assistive devices in order to identify the PopoverClose.
   */
  accessibilityText?: string
  /**
   * Set to true to use PopoverClose on dark backgrounds.
   */
  dark?: boolean
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * A React ref to assign to the HTML node representing the PopoverClose element.
   */
  forwardedRef?: ButtonForwardRef
  /**
   * Denotes this PopoverClose is being used with the Overlay. Olive only.
   * Overlay requires a larger close icon for Olive, as well as specific styling.
   */
  isForOverlay?: boolean
  /**
   *  Adds an optional scrim styled wrapper around the PopoverClose for when it sits on top of an image. IE ModalClose in ImageModal.
   */
  scrim?: boolean
  /**
   *  A smaller version of PopoverClose the utilizes the IconSmall component.
   */
  small?: boolean
}

export function PopoverClose({
  accessibilityText,
  dark = false,
  'data-qa': dataQa,
  forwardedRef,
  isForOverlay,
  scrim = false,
  onClick,
  small = false,
  ...restProps
}: PopoverCloseProps) {
  const sx = useThemeStyles(styles, { small, dark, scrim, isForOverlay })
  const translate = useTranslate()

  const icondataQa = dataQa && `${dataQa}-icon`

  const inkIcon = small ? (
    <CloseSmall data-qa={icondataQa} />
  ) : (
    <Close data-qa={icondataQa} />
  )

  const oliveIcon = isForOverlay ? (
    <CloseXlargeOlive data-qa={icondataQa} />
  ) : (
    <CloseOlive data-qa={icondataQa} />
  )

  return (
    <div css={sx.default.wrap}>
      <IconButton
        {...dataProps(restProps)}
        {...onProps(restProps)}
        data-qa={dataQa}
        accessibilityText={accessibilityText || translate('OLIVE:CLOSE')}
        disableTooltip
        forwardedRef={forwardedRef}
        onClick={onClick}
        size={small ? 'small' : 'medium'}
        icon={<IconWithTheme inkIcon={inkIcon} oliveIcon={oliveIcon} />}
      />
    </div>
  )
}

PopoverClose.propTypes = {
  accessibilityText: PropTypes.string,
  dark: PropTypes.bool,
  'data-.*': PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  isForOverlay: PropTypes.bool,
  'on[A-Z].*': PropTypes.func,
  onClick: PropTypes.func,
  scrim: PropTypes.bool,
  small: PropTypes.bool,
}

PopoverClose.displayName = 'PopoverClose'
